import * as React from 'react'
import { TechnicalOrder } from '@models/technical-orders'
import Table, { TableFilters, TableHeaderValue } from '@components/table/table'
import { TechnicalOrdersTodoOptions } from '@modules/technical-orders/technical-orders-todo/options'
import { Col } from 'reactstrap'
import { useSelectableRows } from '@components/table/use-selectable-rows'
import { TechnicalOrdersToAcceptTableRow } from '@modules/technical-orders/technical-orders-to-accept/row'
import { useGetTechnicalOrdersQuery } from '@api/technical-orders'
import { TechnicalOrdersTodoFilters } from '@modules/technical-orders/technical-orders-todo/filters'
import { TechnicalOrderTableUnitHeader } from '@modules/technical-orders/common/table-unit-header'
import { TechnicalOrderTableTypeHeader } from '@modules/technical-orders/common/table-type-header'
import { filterTableHeaders } from '@modules/technical-orders/helpers'
import { TechnicalOrderTableUsersHeader } from '@modules/technical-orders/common/table-users-header'
import { TableViewType, OrderTableViewSwitch } from '@modules/technical-orders/common/table-view-switch'
import { TechnicalOrderTableGrupped } from '@modules/technical-orders/common/table-grupped'
import { TypedQueryResult } from '@api/base'

interface Props {
  priority: 0 | 10
  resortId: number
}

const defaultFilters: TableFilters = {
  search: '',
  ordering: '-finished_at',
  page: 1,
  page_size: 9999,
}

const emptyList = []

export const TechnicalOrdersToAcceptTable: React.FC<Props> = ({ priority, resortId }) => {
  const [filters, setFilters] = React.useState<TableFilters>(defaultFilters)
  const [tableView, setTableView] = React.useState<TableViewType>('list')

  const headers: TableHeaderValue[] = React.useMemo(
    () =>
      filterTableHeaders(
        [
          { title: 'Zespół', sortField: 'staff' },
          { title: 'Nr zadania', sortField: 'number', width: 165 },
          { title: 'Lokalizacja', sortField: 'resort_id', width: 150 },
          { title: <TechnicalOrderTableUnitHeader resortId={resortId} setFilters={setFilters} />, width: 150 },
          { title: 'Czas pracy', width: 180 },
          { title: <TechnicalOrderTableTypeHeader setFilters={setFilters} />, width: 250 },
          {
            title: <TechnicalOrderTableUsersHeader setFilters={setFilters} resortId={resortId} />,
            sortField: 'users',
            width: 250,
          },
          { title: '', width: 70 },
          { title: ' ' },
        ],
        resortId,
        tableView,
      ),
    [resortId, tableView],
  )

  const { data: technicalOrders = emptyList } = useGetTechnicalOrdersQuery({
    resort: resortId,
    status: 'to_accept',
    priority,
    ...filters,
  }) as TypedQueryResult<TechnicalOrder[]>

  const { selectableHeader, selectableCell, clearSelectedRows, selectedRows } = useSelectableRows<TechnicalOrder>(
    technicalOrders,
    [],
    {
      tableHeaderClassName: 'bg-white border-top-0 vertical-align-middle',
      selectableCellClassName: 'vertical-align-middle',
    },
  )

  return (
    <>
      <TechnicalOrdersTodoFilters filters={filters} setFilters={setFilters} defaultFilters={defaultFilters}>
        <Col md={6}>
          <TechnicalOrdersTodoOptions
            tab="to_accept"
            clearSelectedRows={clearSelectedRows}
            selectedRows={selectedRows}
          />
        </Col>
        <OrderTableViewSwitch tableView={tableView} setTableView={setTableView} />
      </TechnicalOrdersTodoFilters>
      <Table
        headerClassName="bg-white border-top-0 vertical-align-middle"
        className="w-100"
        headerValues={headers}
        isLoading={false}
        striped={tableView === 'list'}
        showPagination={false}
        filters={filters}
        setFilters={setFilters}
        selectableHeader={tableView === 'list' && selectableHeader}
      >
        {tableView === 'list' ? (
          technicalOrders.map(technicalOrder => (
            <TechnicalOrdersToAcceptTableRow
              key={technicalOrder.id}
              showResort={!resortId}
              technicalOrder={technicalOrder}
              showDragHandler={false}
              showUsers={true}
            >
              {selectableCell(technicalOrder)}
            </TechnicalOrdersToAcceptTableRow>
          ))
        ) : (
          <TechnicalOrderTableGrupped
            Row={TechnicalOrdersToAcceptTableRow}
            technicalOrders={technicalOrders}
            resortId={resortId}
          />
        )}
      </Table>
    </>
  )
}
